import React from "react";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { HotelBedInfo } from "main/javascripts/components/hotel/HotelBedInfo";
import { useTranslation } from "react-i18next";
import { FullScreenModalDialog } from "main/javascripts/components/molecules/Modal/FullScreenModalDialog";
import { HotelPlan } from "main/javascripts/models/HotelPlan";
import { HotelPlanContent } from "./HotelPlanContent";
import { ImageList } from "main/javascripts/components/spot/ImageList";
import {
  isDesktop,
  moreThanBreakpoint,
} from "main/javascripts/styles/base/responsiveStyle";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import {
  isDisplayedRoomDetailModalSelector,
  roomDetailModalDataSelector,
  setDisplayHotelRoomDetailModal,
} from "main/javascripts/features/hotel/hotelSlice";
import { hotelShowParamSelector } from "main/javascripts/slices/paramSlice";
import {
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { loggedInSocialMediaSelector } from "main/javascripts/slices/currentUserSlice";

export const HotelRoomDetailModal: React.FC = (): React.ReactElement => {
  const appDispatch = useAppDispatch();
  const { t } = useTranslation(["label"]);

  const roomDetailData = useAppSelector(roomDetailModalDataSelector);
  const isDisplayed = useAppSelector(isDisplayedRoomDetailModalSelector);
  const param = useAppSelector(hotelShowParamSelector);

  const loggedInSocialMedia = useAppSelector(loggedInSocialMediaSelector);

  // TODO: 旧reducer
  const breakpoint = useSelector<any>(
    (state) => state.breakpointReducer.breakpoint
  );

  const closeHandler: () => void = () => {
    appDispatch(setDisplayHotelRoomDetailModal(false));
  };

  const onSelectHotelPlan: (hasCostMessage: boolean) => void = (
    hasCostMessage: boolean
  ) => {
    if (roomDetailData.onSelectHotelPlan) {
      roomDetailData.onSelectHotelPlan(hasCostMessage);
      appDispatch(setDisplayHotelRoomDetailModal(false));
    }
  };

  const renderAmenities = (): JSX.Element | null => {
    const { hotelRoom } = roomDetailData;
    if (hotelRoom && hotelRoom.hotelRoomAmenities) {
      return (
        <div css={facilityBlockStyle}>
          <div css={nameStyle}>{hotelRoom.name}</div>
          <div css={facilityItemStyle}>
            <div css={facilityTitleStyle}>{t("label:hotel.bedType")}</div>
            <div css={facilityContentStyle}>
              <HotelBedInfo bedGroups={hotelRoom.bedGroups} />
            </div>
          </div>
          <div css={facilityItemStyle}>
            <div css={facilityTitleStyle}>{t("label:hotel.roomAmenity")}</div>
            <ul css={amenityListStyle}>
              {hotelRoom.hotelRoomAmenities.map(
                (amenity: string, index: number) => (
                  <li css={amenityListItemStyle} key={index}>
                    {amenity}
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderPlans = (): JSX.Element | null => {
    const { hotelId, hotelRoom } = roomDetailData;
    if (hotelRoom && hotelRoom.hotelRoomAmenities) {
      return (
        <div css={planBlockStyle}>
          <h3 css={planTitleStyle}>お部屋のプラン</h3>
          <div css={planInnerStyle}>
            {hotelRoom.hotelPlans?.map(
              (hotelPlan: HotelPlan, index: number) => {
                return (
                  <HotelPlanContent
                    key={index}
                    hotelRoomId={hotelRoom.id}
                    hotelId={hotelId}
                    hotelPlan={hotelPlan}
                    param={param}
                    onSelectHotelPlan={onSelectHotelPlan}
                    loggedInSocialMedia={loggedInSocialMedia}
                  />
                );
              }
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderImages = (): JSX.Element | null => {
    const { hotelRoom } = roomDetailData;
    const imageListOptions = {
      autoplay: false,
    };
    return (
      <div>
        <ImageList
          images={hotelRoom.hotelRoomImages}
          options={imageListOptions}
        />
      </div>
    );
  };

  const renderHeader = (): JSX.Element | null => {
    return (
      <div css={headerStyle}>
        <div css={headerTitleStyle}>部屋情報</div>
      </div>
    );
  };

  return (
    <FullScreenModalDialog
      displayed={isDisplayed}
      closeHandler={closeHandler}
      colorMode="light"
      header={renderHeader()}
    >
      {roomDetailData && roomDetailData.hotelRoom && (
        <>
          <div css={scrollerStyle}>
            <div css={innerStyle}>
              <div css={firstBlockStyle}>
                {!isDesktop(breakpoint) && renderImages()}
                {renderAmenities()}
              </div>
              <div css={secondBlockStyle}>
                {isDesktop(breakpoint) && renderImages()}
                {renderPlans()}
              </div>
            </div>
          </div>
        </>
      )}
    </FullScreenModalDialog>
  );
};

const headerStyle = css`
  position: absolute;
  width: 100%;
  height: 3rem;
  border-bottom: 1px solid ${borderColor.primaryLightColor};
  z-index: 10;
  top: 0;
  left: 0;
  padding: 0 ${space.atom1_5x};
  box-sizing: border-box;
`;
const headerTitleStyle = css`
  font-size: ${fontSize.mediumHeading};
  letter-spacing: 0.05em;
  padding: ${space.atom1_5x} ${space.atom};
  color: ${textColor.secondaryDarkColor};
  ${moreThanBreakpoint("desktop")} {
    padding: ${space.atom1_5x};
  }
`;
const scrollerStyle = css`
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;
const innerStyle = css`
  padding: ${space.atom} 0;
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom} 2vw;
  }
  ${moreThanBreakpoint("desktop")} {
    display: flex;
    max-width: 90rem;
    margin: 0 auto;
    padding: ${space.atom};
  }
`;
const firstBlockStyle = css`
  padding: ${space.atom};
  ${moreThanBreakpoint("desktop")} {
    width: 40%;
  }
`;
const secondBlockStyle = css`
  padding: ${space.atom};
  ${moreThanBreakpoint("desktop")} {
    width: 60%;
  }
`;
const facilityBlockStyle = css`
  font-size: ${fontSize.body};
  color: ${textColor.secondaryDarkColor};
`;
const nameStyle = css`
  font-size: ${fontSize.headline};
  color: ${textColor.primaryDarkColor};
  font-weight: ${fontWeight.bold};
  padding: ${space.atom2x} ${space.atom} ${space.atom};
  margin-bottom: ${space.atom};
  &:after {
    content: "";
    display: block;
    width: calc(100% - ${space.atom2x});
    height: 1px;
    background-color: ${borderColor.primaryLightColor};
    margin-top: ${space.atom2x};
  }
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.title};
  }
  ${moreThanBreakpoint("desktop")} {
    padding-top: ${space.atom};
  }
`;
const facilityItemStyle = css`
  padding: 0 0 ${space.atom};
`;
const facilityTitleStyle = css`
  color: ${textColor.primaryDarkColor};
  font-size: ${fontSize.mediumHeading};
  font-weight: ${fontWeight.bold};
  padding: ${space.atom2x} ${space.atom} ${space.atom0_5x};
`;
const facilityContentStyle = css`
  padding: ${space.atom};
`;
const amenityListStyle = css`
  list-style-type: square;
  margin: 0;
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body1};
  display: flex;
  flex-wrap: wrap;
  padding: ${space.atom};
`;
const amenityListItemStyle = css`
  position: relative;
  list-style: none;
  padding: ${space.atom0_5x} ${space.atom} ${space.atom0_5x} ${space.atom2_5x};
  width: calc(100% - ${space.atom2_5x});
  box-sizing: border-box;
  &:before {
    content: "";
    display: block;
    position: absolute;
    width: ${space.atom1_5x};
    height: 1px;
    background-color: ${borderColor.secondaryLightColor};
    left: 0;
    top: 1em;
  }
  ${moreThanBreakpoint("tablet")} {
    width: calc(50% - ${space.atom2_5x});
  }
  ${moreThanBreakpoint("desktop")} {
    width: calc(100% - ${space.atom2_5x});
  }
  ${moreThanBreakpoint("widescreen")} {
    width: calc(50% - ${space.atom2_5x});
  }
`;
const planBlockStyle = css`
  padding: ${space.atom2x} ${space.atom};
`;
const planInnerStyle = css`
  border: 1px solid ${borderColor.primaryLightColor};
`;
const planTitleStyle = css`
  font-size: ${fontSize.headline};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.05em;
  padding: ${space.atom} 0 ${space.atom2x};
`;
